import React, {ReactElement} from "react";
import {Link} from "react-router";
import {IconButton, Tooltip} from "@material-ui/core";
import Skeleton from "@material-ui/lab/Skeleton";
import LinkIcon from "@material-ui/icons/Link";
import ArchiveIcon from "@material-ui/icons/Archive";
import {styled} from "styled-components";

import calculateDuration from "routes/llm_run/utils/calculate_duration";
import TestStatus from "routes/llm_run/llm_run_list/components/test_status";
import getStatusIcon from "routes/data_assets/util/get_status_icon";

import {LlmRunId} from "common/flowmaster/types/llm_run";
import {OrganisationId} from "common/types/organisation";
import {DataAssetRunInfo} from "common/flowmaster/types/data_asset";
import renderDuration from "routes/llm_run/utils/render_duration";
import {Definition, DefinitionConfig} from "./data_assets_create_view";
import InputCircleIcon from "icons/InputCircle";
import OutputCircleIcon from "icons/OutputCircle";
import FilterableName from "common_components/flowmaster/llm_run/filtering/filterable_name";

export interface ConfiguraitonRunsProps {
  definitions: Definition[];
  configurations: DefinitionConfig[];
  selectedDefinitionId: Definition["id"] | null;
  selectedConfig: DefinitionConfig;
  runs: DataAssetRunInfo[];
  isLoadingRuns: boolean;
  error: string | null;
  selectedRunId: DataAssetRunInfo["run_id"] | null;
  setSelectedRunId: (id: LlmRunId | null) => void;
  organisationId: OrganisationId;
  updateValues: (pairs: Record<string, unknown | undefined | null>) => void;
}

const ListLink = styled(Link)`
  color: inherit;
  text-decoration: none;
  pointer: cursor;
`;

const Container = styled.div`
  margin-top: 1em;
  border: 1px solid #ddd;
  border-radius: 4px;
`;

const Table = styled.table`
  border-collapse: collapse;
  th {
    padding: 1em;
  }
  width: 100%;
`;

const TableRow = styled.tr<{selected?: boolean}>`
  cursor: pointer;

  background-color: ${({selected}) => (selected ? "#ffffdd" : "transparent")};

  &:nth-child(even) {
    background-color: ${({selected}) => (selected ? "#ffffdd" : "#fcfcfc")};
  }

  &:nth-child(odd) {
    background-color: ${({selected}) => (selected ? "#ffffdd" : "#eef")};
  }

  &:hover {
    background-color: #ffffdd;
  }
`;

const TableCell = styled.td`
  padding: 1em 0.5em;
  border-top: 1px solid #aaa;
  text-align: center;
`;

const IconIdentifier = styled.span`
  font-size: 14px;
  font-weight: 600;
  position: relative;
  left: -2px;
  bottom: -4px;
  color: rgb(0, 0, 0, 0.54);
`;

const ConfigurationRuns = (props: ConfiguraitonRunsProps) => {
  return (
    <Container>
      <ConfigurationRunsContent {...props} />
    </Container>
  );
};

const ConfigurationRunsContent = ({
  definitions,
  configurations,
  selectedDefinitionId,
  selectedConfig,
  runs,
  isLoadingRuns,
  error,
  selectedRunId,
  setSelectedRunId,
  organisationId,
  updateValues,
}: ConfiguraitonRunsProps) => {
  return (
    <ConfigurationRunsTable
      definitions={definitions}
      configurations={configurations}
      selectedDefinitionId={selectedDefinitionId}
      selectedConfig={selectedConfig}
      runs={runs}
      isLoading={isLoadingRuns}
      error={error}
      selectedRunId={selectedRunId}
      setSelectedRunId={setSelectedRunId}
      organisationId={organisationId}
      updateValues={updateValues}
    />
  );
};

const ConfigurationRunsSkeleton = () => {
  return (
    <tr>
      <TableCell style={{padding: "1em 0em"}} colSpan={9}>
        <Skeleton height={"2em"} />
        <Skeleton height={"2em"} />
        <Skeleton height={"2em"} />
      </TableCell>
    </tr>
  );
};

const ConfigurationRunsTable = ({
  definitions,
  configurations,
  selectedDefinitionId,
  selectedConfig,
  runs,
  isLoading,
  error,
  selectedRunId,
  setSelectedRunId,
  organisationId,
  updateValues,
}) => {
  return (
    <Table>
      <thead>
        <tr
          style={{
            backgroundColor: "#fff",
          }}
        >
          <th>Name</th>
          <th>Date</th>
          <th>User</th>
          <th>Project</th>
          <th>Document</th>
          <th>Status</th>
          <th>Tests</th>
          <th>Duration</th>
          <th>Cost</th>
        </tr>
      </thead>
      <tbody>
        {renderRows(
          definitions,
          configurations,
          selectedDefinitionId,
          selectedConfig,
          runs,
          isLoading,
          error,
          selectedRunId,
          setSelectedRunId,
          organisationId,
          updateValues,
        )}
      </tbody>
    </Table>
  );
};

function renderRows(
  definitions: Definition[],
  configurations: DefinitionConfig[],
  selectedDefinitionId: Definition["id"],
  selectedConfig: DefinitionConfig["id"],
  runs: DataAssetRunInfo[],
  isLoading: boolean,
  error: string | null,
  selectedRunId: DataAssetRunInfo["run_id"] | null,
  setSelectedRunId: (id: LlmRunId | null) => void,
  organisationId: OrganisationId,
  updateValues: (pairs: Record<string, unknown | undefined | null>) => void,
): ReactElement | ReactElement[] {
  if (isLoading) {
    return <ConfigurationRunsSkeleton />;
  }

  if (error) {
    return (
      <tr>
        <TableCell
          colSpan={9}
          style={{padding: "1em", color: "#f44336", textAlign: "center"}}
        >
          Error: {error}
        </TableCell>
      </tr>
    );
  }

  if (runs.length === 0) {
    return (
      <tr>
        <TableCell colSpan={9} style={{padding: "1em", textAlign: "center"}}>
          No runs for this configuration.
        </TableCell>
      </tr>
    );
  }

  const rows: ReactElement[] = [];

  runs.forEach((run, idx) => {
    rows.push(
      renderDataAssetRun(
        definitions,
        configurations,
        selectedDefinitionId,
        selectedConfig,
        run,
        idx,
        run.run_id === selectedRunId,
        setSelectedRunId,
        organisationId,
        updateValues,
      ),
    );

    const shouldAddDivider =
      idx === 0 &&
      runs.length > 1 &&
      new Date(run.creation_date) < new Date(runs[1].creation_date);

    if (shouldAddDivider) {
      rows.push(
        <tr key="most-recent-divider">
          <TableCell
            colSpan={9}
            style={{
              padding: "0.5em 1em",
              textAlign: "center",
              fontWeight: 500,
              fontSize: "0.9em",
              color: "#666",
            }}
          >
            Latest runs (filtered by date and max runs)
          </TableCell>
        </tr>,
      );
    }
  });

  return rows;
}

function renderDataAssetRun(
  definitions,
  configurations,
  selectedDefinitionId,
  selectedConfig,
  run,
  idx: number,
  isSelected: boolean,
  setSelectedRunId,
  organisationId,
  updateValues,
) {
  const runConfig = configurations.find(
    c => c.id === run.data?.workflow_instance_id,
  );

  const precedenceIcon = getDefinitionPrecedenceIcon(
    selectedDefinitionId,
    run.data?.workflow_id,
    definitions,
  );
  const configPrecedenceIcon = getConfigPrecedenceIcon(
    selectedConfig,
    runConfig,
  );

  const projectUrl = `/organisation/${organisationId}/project/${run.project.id}/document/list`;
  const documentUrl = `/organisation/${organisationId}/project/${run.project.id}/document/${run.document.id}/detail`;

  return (
    <TableRow
      selected={isSelected}
      key={`asset-run-${idx}`}
      onClick={() => setSelectedRunId(isSelected ? null : run.run_id)}
    >
      <TableCell>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              gap: "1em",
            }}
          >
            {run.name}
            {runConfig.is_archived && (
              <ArchiveIcon style={{color: "rgba(0, 0, 0, 0.54)"}} />
            )}
          </div>
          <div style={{display: "flex"}}>
            {configPrecedenceIcon && !precedenceIcon && (
              <div style={{display: "flex"}}>
                {configPrecedenceIcon}
                <ListLink
                  onClick={e => e.stopPropagation()}
                  to={`/organisation/${organisationId}/data-assets/definition/${selectedDefinitionId}/config/${run.data.workflow_instance_id}`}
                >
                  <div
                    style={{
                      display: "flex",
                      width: "100%",
                      justifyContent: "center",
                    }}
                  >
                    <IconButton size="small" style={{padding: 0}}>
                      <LinkIcon />
                    </IconButton>
                  </div>
                </ListLink>
              </div>
            )}
            {precedenceIcon && (
              <div style={{display: "flex"}}>
                {precedenceIcon}
                <ListLink
                  onClick={e => e.stopPropagation()}
                  to={`/organisation/${organisationId}/data-assets/definition/${run.data.workflow_id}/config/${run.data.workflow_instance_id}`}
                >
                  <div
                    style={{
                      display: "flex",
                      width: "100%",
                      justifyContent: "center",
                    }}
                  >
                    <IconButton size="small" style={{padding: 0}}>
                      <LinkIcon />
                    </IconButton>
                  </div>
                </ListLink>
              </div>
            )}
          </div>
        </div>
      </TableCell>
      <TableCell>{new Date(run.creation_date).toLocaleString()}</TableCell>
      <TableCell>
        <FilterableName
          name={run.username}
          filter={() => updateValues({user: run.username})}
        />
      </TableCell>
      <TableCell>
        <FilterableName
          name={run.project.name}
          url={projectUrl}
          filter={() => updateValues({project: run.project.id})}
        />
      </TableCell>
      <TableCell>
        <FilterableName
          name={run.document.name}
          url={documentUrl}
          filter={() =>
            updateValues({document: run.document.id, project: run.project.id})
          }
        />
      </TableCell>
      <TableCell>
        <ListLink
          onClick={e => e.stopPropagation()}
          to={`/organisation/${organisationId}/llm_run/${run.run_id}/detail`}
        >
          <div
            style={{display: "flex", width: "100%", justifyContent: "center"}}
          >
            {getStatusIcon(run)}
            <IconButton size="small" style={{padding: 0}}>
              <LinkIcon />
            </IconButton>
          </div>
        </ListLink>
      </TableCell>
      <TableCell>
        <TestStatus stats={run.stats} />
      </TableCell>
      <TableCell>
        {renderDuration(calculateDuration(run.creation_date, run.last_edited))}
      </TableCell>
      <TableCell>{`$${run.total_cost.toFixed(4)}`}</TableCell>
    </TableRow>
  );
}

function getDefinitionPrecedenceIcon(
  selectedDefinitionId,
  runDefinitionId,
  definitions,
) {
  const selectedDefinition = definitions.find(
    def => def.id === selectedDefinitionId,
  );
  const runDefinition = definitions.find(def => def.id === runDefinitionId);

  if (!selectedDefinition || !runDefinition) {
    return null;
  }

  if (runDefinition.creation_date > selectedDefinition.creation_date) {
    return (
      <Tooltip title="Newer definition">
        <div>
          <InputCircleIcon />
          <IconIdentifier>D</IconIdentifier>
        </div>
      </Tooltip>
    );
  }

  if (runDefinition.creation_date < selectedDefinition.creation_date) {
    return (
      <Tooltip title="Older definition">
        <div>
          <OutputCircleIcon />
          <IconIdentifier>D</IconIdentifier>
        </div>
      </Tooltip>
    );
  }

  return null;
}

function getConfigPrecedenceIcon(
  selectedConfig: DefinitionConfig | undefined,
  runConfig: DefinitionConfig | undefined,
) {
  if (!selectedConfig || !runConfig) {
    return null;
  }

  if (runConfig.creation_date > selectedConfig.creation_date) {
    return (
      <Tooltip title="Newer configuration on the selected definition">
        <div>
          <InputCircleIcon />
          <IconIdentifier>C</IconIdentifier>
        </div>
      </Tooltip>
    );
  }

  if (runConfig.creation_date < selectedConfig.creation_date) {
    return (
      <Tooltip title="Older configuration on the selected definition">
        <div>
          <OutputCircleIcon />
          <IconIdentifier>C</IconIdentifier>
        </div>
      </Tooltip>
    );
  }

  return null;
}

export default ConfigurationRuns;
